import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { DEFAULT_PAYCHECK_CENTS, DEFAULT_PAYCHECK_COUNT } from 'prototypes/securedCard/prototypeAssumptions';
import {
  buildLedger,
  clampPaycheckCents,
  clampPaycheckCount,
  milestoneRank,
  projectStory,
} from 'prototypes/securedCard/storyMath';
import { LedgerEntry, PayCadence, StoryMilestone, StoryProjection, StoryState } from 'prototypes/securedCard/types';

const STORAGE_KEY = 'plannery-secured-card-prototype-v2';

const defaultState = (): StoryState => ({
  milestone: StoryMilestone.NotStarted,
  perPaycheckCents: DEFAULT_PAYCHECK_CENTS,
  cadence: PayCadence.Biweekly,
  paycheckCount: DEFAULT_PAYCHECK_COUNT,
});

const isMilestone = (value: unknown): value is StoryMilestone =>
  typeof value === 'string' && Object.values(StoryMilestone).includes(value as StoryMilestone);

const isCadence = (value: unknown): value is PayCadence =>
  typeof value === 'string' && Object.values(PayCadence).includes(value as PayCadence);

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const isStoryState = (value: unknown): value is StoryState => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    isMilestone(candidate.milestone) &&
    isFiniteNumber(candidate.perPaycheckCents) &&
    isCadence(candidate.cadence) &&
    isFiniteNumber(candidate.paycheckCount)
  );
};

const readStoredState = (): StoryState => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultState();
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isStoryState(parsed)) {
      return defaultState();
    }
    return {
      ...parsed,
      perPaycheckCents: clampPaycheckCents(parsed.perPaycheckCents),
      paycheckCount: clampPaycheckCount(parsed.paycheckCount),
    };
  } catch {
    return defaultState();
  }
};

const advanceTo = (current: StoryState, target: StoryMilestone): StoryState => {
  if (milestoneRank(current.milestone) >= milestoneRank(target)) {
    return current;
  }
  return { ...current, milestone: target };
};

interface StoryContextValue {
  state: StoryState;
  projection: StoryProjection;
  ledger: LedgerEntry[];
  begin: (cents: number, cadence: PayCadence, paycheckCount: number) => void;
  setPerPaycheckCents: (cents: number) => void;
  setCadence: (cadence: PayCadence) => void;
  setPaycheckCount: (count: number) => void;
  confirmPaycheck: () => void;
  confirmGrowth: () => void;
  confirmPayment: () => void;
  finish: () => void;
  resetStory: () => void;
}

const StoryContext = createContext<StoryContextValue | null>(null);

export const StoryProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<StoryState>(readStoredState);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const projection = useMemo(
    () =>
      projectStory({
        perPaycheckCents: state.perPaycheckCents,
        cadence: state.cadence,
        paycheckCount: state.paycheckCount,
      }),
    [state.perPaycheckCents, state.cadence, state.paycheckCount],
  );

  const ledger = useMemo(() => buildLedger(state, projection), [state, projection]);

  const rewindIfPastSetup = (current: StoryState): StoryMilestone => {
    if (milestoneRank(current.milestone) > milestoneRank(StoryMilestone.PaycheckSet)) {
      return StoryMilestone.PaycheckSet;
    }
    return current.milestone;
  };

  const value = useMemo<StoryContextValue>(
    () => ({
      state,
      projection,
      ledger,
      begin: (cents: number, cadence: PayCadence, paycheckCount: number) => {
        setState({
          milestone: StoryMilestone.Started,
          perPaycheckCents: clampPaycheckCents(cents),
          cadence,
          paycheckCount: clampPaycheckCount(paycheckCount),
        });
      },
      setPerPaycheckCents: (cents: number) => {
        setState((current) => {
          const perPaycheckCents = clampPaycheckCents(cents);
          if (perPaycheckCents === current.perPaycheckCents) {
            return current;
          }
          return { ...current, perPaycheckCents, milestone: rewindIfPastSetup(current) };
        });
      },
      setCadence: (cadence: PayCadence) => {
        setState((current) => {
          if (cadence === current.cadence) {
            return current;
          }
          return { ...current, cadence, milestone: rewindIfPastSetup(current) };
        });
      },
      setPaycheckCount: (count: number) => {
        setState((current) => {
          const paycheckCount = clampPaycheckCount(count);
          if (paycheckCount === current.paycheckCount) {
            return current;
          }
          return { ...current, paycheckCount };
        });
      },
      confirmPaycheck: () => setState((current) => advanceTo(current, StoryMilestone.PaycheckSet)),
      confirmGrowth: () => setState((current) => advanceTo(current, StoryMilestone.GrowthSeen)),
      confirmPayment: () => setState((current) => advanceTo(current, StoryMilestone.PurchasePaid)),
      finish: () => setState((current) => advanceTo(current, StoryMilestone.Complete)),
      resetStory: () => setState(defaultState()),
    }),
    [state, projection, ledger],
  );

  return <StoryContext.Provider value={value}>{children}</StoryContext.Provider>;
};

export const useStory = (): StoryContextValue => {
  const value = useContext(StoryContext);
  if (!value) {
    throw new Error('useStory must be used within StoryProvider');
  }
  return value;
};
